
import { Agent } from './Agent';
import { Device } from './Device';
import { EventEmitter } from "./EventEmitter";
import { Conference } from './Conference';
import * as anCti from "./ancti";

type LocalTracks = {
    audio?: MediaStreamTrack,
    video?: MediaStreamTrack,
    display?: MediaStreamTrack
}

type DescriptionType = {
    common: string[],
    audio: string[],
    video: string[]
}

export class Call implements anCti.Call {

	readonly agent: Agent;
 	readonly device: Device;

    callID: string;
    number?: string;
	name?: string;
    remoteStream?: MediaStream;
    muted = false;
    localConnectionInfo?: anCti.LocalConnectionState;
    lastRedirection: any;
        
    conference?: Conference;
    pc?: RTCPeerConnection;
    localStream?: MediaStream;
    localTracks: LocalTracks = {
        audio: undefined,
        video: undefined,
        display: undefined,
    };
    
    separateStreams?: boolean;

    remoteVideoStream?: MediaStream;
    remoteAudioStream?: MediaStream;
    localVideoStream?: MediaStream;
    localAudioStream?: MediaStream;

    audioConstraints?: MediaTrackCapabilities;
    videoConstraints?: MediaTrackConstraints;
    displayConstraints?: anCti.GetDisplayMediaConstraints;
    
    remoteAudioTrackProperties?: MediaStreamTrack;
    remoteVideoTrackProperties?: MediaStreamTrack;
   
    audioReceiverProperties?: RTCRtpReceiver;
    videoReceiverProperties?: RTCRtpReceiver;

    rtcConfiguration: any;

    state: anCti.CallState = "idle";
    autoAnswer?: boolean|string;
    audioDirection?: string;
    videoOn?: boolean;
    displayOn?: boolean;
    processingRtc?: boolean;
    withoutCamera: boolean = false;
  
    readonly globallyUniqueCallLinkageID?: string;

    protected eventEmitter: EventEmitter;
    protected localDtmfSender: any;
    
    protected candidatePolicy: string = "none";
    protected candidates: any[] = [];

    protected fetchConnectedNumber: boolean = false;

    // https://w3c.github.io/webrtc-pc/#perfect-negotiation-example
    protected negotiationNeeded: boolean = false;
    protected makingOffer: boolean = false;
    protected ignoreOffer: boolean = false;
    protected isSettingRemoteAnswerPending: boolean = false;
    protected polite: boolean = false;

    private externalAudioSessionDescription?: Promise<RTCSessionDescription>;

    constructor(agent: Agent, device: Device,callID: string) {
      	this.agent = agent;
        this.device = device;
        this.callID = callID;
        this.separateStreams = agent.separateStreams;

        // as default use the values from the agent
        this.remoteAudioTrackProperties = agent.remoteAudioTrackProperties;
        this.remoteVideoTrackProperties = agent.remoteVideoTrackProperties;
        this.videoConstraints = agent.videoConstraints;
        this.audioConstraints = agent.audioConstraints;
        this.displayConstraints = agent.displayConstraints;
        this.audioReceiverProperties = agent.audioReceiverProperties;
        this.videoReceiverProperties = agent.videoReceiverProperties;
        this.rtcConfiguration = agent.rtcConfiguration;

        this.eventEmitter = new EventEmitter(device.eventEmitter);
    }

    stringify() {
		return `[Call ${this.callID}]`;
	}

     // internal, used by agent
    processEvent(name: string, content: any) {
        switch (name) {
            case 'OriginatedEvent':
                this.fetchConnectedNumber = true;
                this.agent.parseDeviceID(content.calledDevice,this);
                break;
            case 'DeliveredEvent':
                if (content?.localConnectionInfo=="alerting") {
					this.agent.parseDeviceID(content.callingDevice, this);
				} else {
                	this.agent.parseDeviceID(content.calledDevice, this);
				    this.fetchConnectedNumber = true;
                }
                break;
            case 'EstablishedEvent':
                if (this.fetchConnectedNumber) {
                    this.agent.parseDeviceID(content.answeringDevice, this);
                    this.fetchConnectedNumber = false;
                } else {
                    // fetch remote identity
                    if (content.callingDevice.deviceIdentifier==this.device.deviceID) {
                        this.agent.parseDeviceID(content.answeringDevice, this);
                    } else {         
                        this.agent.parseDeviceID(content.callingDevice, this);
                    }
                }
                break;
            case 'TransferedEvent': // ecma style ...
            case 'TransferredEvent': {
                // check if callID changed
                if (content.transferredConnections) {
                    for (let e of content.transferredConnections) {
                        if (e.connectionListItem.newConnection?.callID && e.connectionListItem.oldConnection) {
                            this.callID = e.connectionListItem.newConnection.callID;
                            break;
                        }
                    }
                }

                this.agent.parseDeviceID(content.transferredToDevice,this);
                break;
            }
            case 'ConferencedEvent': {
                // check if callID changed
                let peers = [];
                for (let e of content.conferenceConnections) {
                    let peer = this.agent.parseDeviceID(e.connectionListItem.newConnection.deviceID);
                    peers.push(peer.number);
                  
                    if (peers.length==1) {
                        // on AS the first element is always the own connection
                        let newCallID = e.connectionListItem.newConnection.callID; 
                        if (newCallID!=this.callID) {
                            this.agent.debug(`conferenced callID changes from ${this.callID} to ${newCallID}`);
                            this.callID = newCallID;
                        }
                    }
                }

                // use all numbers of peers
                this.number = peers.join("/");
                break;
            }
        }

        Object.assign(this,content);

        if (this.localConnectionInfo=="null" || this.localConnectionInfo=="fail") {
            this.deletePeerConnection();
            this.device.removeCall(this.callID);
            if (this.agent.customConferences) {
                // application wants to handle conference on its own
            } else {
                this.conference?.close();
            }
        }
        if (content.lastRedirectionDevice?.numberDialed) {
            this.lastRedirection = this.agent.parseDeviceID(content.lastRedirectionDevice.numberDialed);
        }

        // "state" extends the localConnectionInfo with "idle", "calling" and "held"
        // (localConnectionInfo is standardized by ECMA, therefore we should not extend it)
        this.state = this.localConnectionInfo || 'idle';
        switch (this.state) {
            case "initiated":
                this.state = "calling";
                break;
            case "connected":
                switch (this.audioDirection) {
                    case "recvonly":
                    case "inactive":
                        this.state = "held";
                        break;
                }
                break;
        }

        this.notifyEvent(name,content);
    }

    shutdown() {
        // simulate clear-event from server
        this.agent.debug("force shutdown of call",this.callID);
        this.agent.processMessage({ConnectionClearedEvent: {
            cause: "shutdown",
            droppedConnection: {
                callID: this.callID,
                deviceID: this.device.deviceID
            },
            localConnectionInfo: "null",
            monitorCrossRefID: this.device.monitorCrossRefID,
        }});
    }

    setWithoutCamera(withoutCamera: boolean) {
        this.withoutCamera = withoutCamera;
    }

    notifyEvent(name:string,content:any) {
        this.eventEmitter.notify("call", {
            call: this, 
            name: name, 
            device: this.device,
            content: content
        });
    }

    processSnapshotDevice(snapshotDeviceResponseInfo: any) {
        const cstate = snapshotDeviceResponseInfo?.localCallState?.compoundCallState?.localConnectionState;
        if (cstate && !this.localConnectionInfo) {
            this.agent.debug("creating call snapshot call in state",cstate)
            this.localConnectionInfo = cstate;
            this.notifyEvent("SnapshotDeviceResponse",snapshotDeviceResponseInfo);
        }
    }

    processSnapshotCall(snapshotData: any) {
        snapshotData.forEach((entry:any) => {
            if (entry.snapshotCallResponseInfo?.deviceOnCall?.deviceIdentifier==this.device.deviceID) {
                if (entry.snapshotCallResponseInfo?.localConnectionInfo) {
                    this.localConnectionInfo = entry.snapshotCallResponseInfo?.localConnectionInfo;
                }
            } else {
                this.agent.parseDeviceID(entry.snapshotCallResponseInfo.deviceOnCall.deviceIdentifier,this);
            }
        });
        this.notifyEvent("SnapshotCallResponse",snapshotData);
    }

    async answerCall(options?: anCti.AnswerCallOptions) {
        this.polite = true;
   
        let msg = {
            AnswerCall: {
                callToBeAnswered:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                },
                constraints: options,
            }
        }
         
        return this.agent.invoke(msg);
    }
    
   	async holdCall() {
        return this.agent.invoke({
            HoldCall:{
                callToBeHeld:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                }
            }
        });
    }
    
    async retrieveCall() {
        return this.agent.invoke({ 
            RetrieveCall: {
                callToBeRetrieved:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                }
            }
        });
    }
    
    async updateCall(options?: anCti.UpdateCallOptions) {
        let msg:any = {
        	UpdateCall: {
                callToUpdate: {
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                },
                correlatorData: options?.correlatorData,
            }
        }

        if (options?.audio || options?.video || options?.display) {
			msg.UpdateCall.constraints = {
				audio: options?.audio,
				video: options?.video,
                display: options?.display,
			};
		}

        return this.agent.invoke(msg);
    }

	async generateDigits(digits: string,options?: anCti.GenerateDigitsOptions) {
        let msg: any = {
            GenerateDigits: {
				connectionToSendDigits:{
					callID: this.callID,
					deviceID: this.device.deviceID,
				},
    			charactersToSend: digits,
			}
        }

        if (options?.toneDuration) {
            msg.GenerateDigits.toneDuration = options.toneDuration;
        }

        return this.agent.invoke(msg);
	}

    async clearConnection() {
        try  {
            let response: any = await this.agent.invoke({ 
                ClearConnection: {
                    connectionToBeCleared:{
                        callID: this.callID,
                        deviceID: this.device.deviceID,
                    }
                }
            });
            return response;
        } catch(error) {
            this.agent.error(`failed to clearConnection ${this.callID}`,error);
            this.shutdown();
            throw error;
        }
    }

    async singleStepTransferCall(dest: string) {
        return this.agent.invoke({ 
            SingleStepTransferCall:{
                activeCall:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                },
                transferredTo: dest,
            }
        });
    }

    async deflectCall(dest: string) {
        return this.agent.invoke({ 
            DeflectCall:{
                callToBeDiverted:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                },
                newDestination: {
                    device: dest,
                }
            }
        });
    }

    async directedPickupCall(dest: string) {
       return this.agent.invoke({
            DirectedPickupCall:{
                callToBePickedUp:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                },
                requestingDevice: dest,
            }
        });
    }

    async transferCall(otherCall: Call): Promise<anCti.TransferCallResponse> {
        return this.agent.invoke({
            TransferCall:{
                activeCall:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                },
                heldCall: {
                    callID: otherCall.callID,
                    deviceID: otherCall.device.deviceID,
                },
            }
        });
    }

    async conferenceCall(otherCall: Call): Promise<anCti.ConferenceCallResponse> {
        return this.agent.invoke({
            ConferenceCall:{
                activeCall:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                },
                heldCall: {
                    callID: otherCall.callID,
                    deviceID: otherCall.device.deviceID,
                },
            }
        });
    }

    async playMessage(messageToBePlayed: string) {
        return this.agent.invoke({
            PlayMessage:{
                overConnection:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                },
                messageToBePlayed: messageToBePlayed,
            }
        });
    }

    async stop(messageToBeStopped: string) {
        return this.agent.invoke({
            Stop:{
                connection:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                },
                messageToBeStopped: messageToBeStopped,
            }
        });
    }

    async recordMessage(options?: anCti.RecordMessageOptions) {
        return this.agent.invoke({
            RecordMessage:{
                callToBeRecorded:{
                    callID: this.callID,
                    deviceID: this.device.deviceID,
                },
                messageID: options?.messageID,
            }
        });
    }

    async snapshotCall(): Promise<anCti.SnapshotCallResponse> {
        let response: any = await this.agent.invoke({
          SnapshotCall: {
            snapshotObject: {
              deviceID: this.device.deviceID,
              callID: this.callID
            }
          }
        });
        if (response.crossRefIDorSnapshotData?.snapshotData?.length) {
          this.processSnapshotCall(response.crossRefIDorSnapshotData.snapshotData);
        }
        return response;
    }

    async restartCall(): Promise<boolean> {
        try {
            this.agent.debug(`restart call ${this.callID} in state ${this.state}`);
            if (this.state=="connected") {
                // instruct peer-connection send STUN/ICE
                this.pc?.restartIce();
                await this.updateCall();
                this.conference?.restartStreams();
            } else {
                await this.snapshotCall();
            }
            return true;
        } catch (error) {
            this.agent.warn("shutdown obsolete call",this.callID,error);
            this.shutdown();
        }
        return false;
    }

    setMuted(value: boolean) {
        this.muted = value;
        if (this.agent.externalAudio) {
            this.agent.externalAudio({
                callID: this.callID,
                type: "update",
                audio: { sender: { enabled: !value }}
            })
        } else if (this.localTracks?.audio) {
            (this.localTracks.audio as any).enabled = !this.muted;
        }
    }

    setSpeakerMuted(value: boolean) {
        if (this.agent.externalAudio) {
            this.agent.externalAudio({
                callID: this.callID,
                type: "update",
                audio: { receiver: { enabled: !value }}
            })
        }
    }

    protected dispose() {
        this.agent.debug("dispose call "+this.callID);
    }
    
    protected patchConstraints(media:string,value:any) {
        if (!value || value==='inactive' || value==='recvonly') {
            return false;
        }
        // enable stream
        let deviceId = this.agent.getMediaDeviceId(media);
        if (deviceId) {
            return { deviceId };
        }
        
        return true;
    }

    protected getTransceiver(kind: string): RTCRtpTransceiver|undefined {
        let transceiver = this.pc?.getTransceivers().find(t => t?.sender?.track?.kind==kind);
        if (!transceiver) {
            // if there is no sender-track yet, let's try the receiver
            transceiver = this.pc?.getTransceivers().find(t => t.receiver?.track?.kind==kind);
        }
        return transceiver;
    }

    setVideoReceiverProperties(value?:RTCRtpReceiver) {
        this.videoReceiverProperties = value;
        if (value) {
            let receiver = this.pc?.getReceivers().find(r => r?.track?.kind=="video");
            receiver && Object.assign(receiver,value);
        }
    }

    setAudioReceiverProperties(value?:RTCRtpReceiver) {
        this.audioReceiverProperties = value;
        if (value) {
            let receiver = this.pc?.getReceivers().find(r => r?.track?.kind=="audio");
            receiver && Object.assign(receiver,value);
        }
    }

    setRemoteVideoTrackProperties(value?:MediaStreamTrack) {
        this.remoteVideoTrackProperties = value;
        if (value) {
            this.remoteVideoStream?.getVideoTracks().forEach(track => Object.assign(track,value));
        }
    }

    setRemoteAudioTrackProperties(value?:MediaStreamTrack) {
        this.remoteAudioTrackProperties = value;
        if (value) {
            this.remoteAudioStream?.getAudioTracks().forEach(track => Object.assign(track,value));
        }
    }


    // internal, used by agent
    processGenerateDigits(tones:string,duration:number,interToneGap?:number) {
        this.agent.debug("generating digits",tones,duration);
        if (!this.localDtmfSender && this.localTracks.audio) {
            const audioTransceiver = this.getTransceiver("audio");
            this.localDtmfSender = audioTransceiver?.sender?.dtmf;
            if (!this.localDtmfSender) {
                this.agent.debug("creating new DTMF sender");
                // fallback to deprecated createDTMFSender
                this.localDtmfSender = (<any>this.pc).createDTMFSender(this.localTracks.audio);
            }
        }
        if (this.localDtmfSender) {
            this.localDtmfSender.ontonechange = (event:any) => this.agent.debug("ontonechange",event);
            this.localDtmfSender.insertDTMF(tones, duration, interToneGap);
        } else if (this.agent.externalAudio) {
            this.agent.externalAudio({
                callID: this.callID,
                type: "generateDigits",
                tones, duration, interToneGap
            });
        } else {
            this.agent.debug("no DTMF support!");
        }
    }

    // internal, used by agent
    async processRtcEvent(content:any) {
        this.processingRtc = true;
		try {
            let remoteDescription = content?.remoteDescription;
            
            if (this.agent.externalAudio) {
                // audio-stream should be handled by this method.
                let useExternalAudio = !!content?.constraints?.audio;
                const audioSdp: any = [];
                const videoSdp: any = [];

                if (remoteDescription?.sdp) {
                    // parse received sdp to split-up audio part
                    let mode = "common";
	                remoteDescription.sdp.split(/[\n\r]+/).forEach((line:string) => {
                        if (this.filterSdpLine(line)) return;
                        if (line.startsWith("m=audio")) {
                            mode = "audio";
                            useExternalAudio = true;
                        } else if (line.startsWith("m=video")) {
                            mode = "video";
                        }
                        mode!="video" && audioSdp.push(line);
                        mode!="audio" && videoSdp.push(line);
                    });
                }

                if (useExternalAudio) {
                    this.externalAudioSessionDescription = this.agent.externalAudio({
                        callID: this.callID,
                        audio: content.constraints?.audio,
                        sdp: this.buildSdpBlock(audioSdp),
                        type: remoteDescription?.type || "create",
                    });

                    // remove audio from requested streams
                    if (content.constraints) {
                        content.constraints.audio = false;
                    }
                    if (remoteDescription?.sdp) {
                        remoteDescription.sdp = this.buildSdpBlock(videoSdp);
                    }
                }
            }

            if (remoteDescription?.type=="close") {
                this.deletePeerConnection();
                this.processingRtc = false;
                return;
            }

            if (!this.pc) {
                if (content?.candidatePolicy) {
                    this.candidatePolicy = content.candidatePolicy;
                }
                this.pc = this.createPeerConnection(this.rtcConfiguration || content?.configuration);
            }

            // first ensure we have the desired streams and tracks
            if (content?.constraints) {
                let userMedia: any = {};
                let newTracks: any = {};
                                  
                for (let media in content.constraints) {
                    if (content.constraints[media]) {
                        // hide proprietary direction and adds configured deviceId
                        userMedia[media] = this.patchConstraints(media,content.constraints[media]);
                    }
                }

                // first create new tracks
                if (userMedia.audio) {
                    try {
                        // if audio is just "true", audioConstraints might be more specific
                        const constraints = (userMedia.audio!==true)?userMedia.audio:this.audioConstraints || true;
                    
                        const stream = await this.agent.getUserMedia(this,{ audio: constraints });
                        this.agent.debug("getUserMedia ended");
                        this.localStream = this.localStream || stream;
                        newTracks.audio = stream?.getAudioTracks()[0];
                        this.agent.debug("new audio track",newTracks.audio);
                    } catch (err) {
                        this.agent.info("could not get audio-track",err);
                        this.agent.notify("getmediaerror",{ call: this, kind: "audio", error: err });
                    }
                }

                if (userMedia.display) {
                    // as MC for now just supports one video-stream we skip video
                    userMedia.video = undefined;

                    try {
                        // if display is just "true", displayConstraints might be more specific
                        const constraints = (userMedia.display!==true)?userMedia.display:this.displayConstraints;
                    
                        // for now no external constraints are supported
                        const stream = await this.agent.getDisplayMedia(this,constraints);
                        this.localStream = this.localStream || stream;
                        newTracks.display = stream?.getVideoTracks()[0];
                        this.agent.debug("new display track",newTracks.display);
                    } catch (err) {
                        this.agent.info("could not get display-media",err)    
                        this.agent.notify("getmediaerror",{ call: this, kind: "display", error: err });
                    }
                }

                if (userMedia.video) {
                    if (this.withoutCamera == true) {
                        this.agent.info("dont aquire video as withoutCamera is set")
                    } else {
                        try {
                            // stop previous track, otherwise some devices refuse to create a new one
                            this.localTracks.video?.stop();

                            // if video is just "true", localVideoConstriaints might be more specific
                            const constraints = (userMedia.video!==true)?userMedia.video:this.videoConstraints || true;
                            const stream = await this.agent.getUserMedia(this, { video: constraints } );
                            this.localStream = this.localStream || stream;
                            newTracks.video = stream?.getVideoTracks()[0];
                            this.agent.debug("new video track",newTracks.video);
                        } catch (err) {
                            this.agent.info("could not get video-track",err)
                            this.agent.notify("getmediaerror",{ call: this, kind: "video", error: err });
                        }
                    }
                }

                for (let media in this.localTracks) {

                    let newTrack : MediaStreamTrack = newTracks[media];
                    let oldTrack: MediaStreamTrack|undefined = this.localTracks[media as keyof LocalTracks];
                    let transceiver = this.getTransceiver(media);
                    let constraints = content.constraints[media];
                    this.localTracks[media as keyof LocalTracks] = newTrack;

                    if (media=='display') {
                        // display track is treated together with video
                        continue;
                    } else if (media=='video') {
                        if (!newTrack && newTracks.display) {
                            // use display-track instead
                            newTrack = newTracks.display;
                            constraints = content.constraints.display;
                        }
                        oldTrack = oldTrack || this.localTracks.display;
                    }
                    
                    if (oldTrack) {
                        this.agent.debug(this.callID,"removing track ",media,oldTrack,transceiver);

                        this.localStream?.removeTrack(oldTrack);

                        if (transceiver?.sender) {
                            await transceiver.sender.replaceTrack(newTrack);
                            if (!newTrack) {
                                this.pc.removeTrack(transceiver.sender);
                            }
                        }
                        
                        oldTrack?.stop();

                        if (!newTrack && transceiver) {
                            // Track is obsolete but we still have a tranceiver.
                            // Ensure we either set the received description or fallback
                            // to "recvonly" to still get the remote stream.
                            if (constraints===true) {
                                // new track failed
                                transceiver.direction = "recvonly";
                            } else {
                                transceiver.direction = constraints || "recvonly";
                            }
                        }
                    }

                    if (newTrack) {
                        this.agent.debug(this.callID,"adding track ",media,newTrack);
                        this.localStream?.addTrack(newTrack);
                        if (transceiver) {
                            // other transceiver of same kind found
                            this.agent.debug(this.callID,"replacing track",media);
                            transceiver.sender.replaceTrack(newTrack);
                        } else if (this.localStream) {
                            this.agent.debug(this.callID,"adding track and setting networkPriority",media);
                            let newSender = this.pc.addTrack(newTrack, this.localStream);
                            const params = newSender.getParameters();
                            if (params.encodings && params.encodings.length > 0) {
                                try {
                                    params.encodings[0].networkPriority = "high";
                                    await newSender.setParameters(params);
                                } catch (e) {
                                    this.agent.warn("cannot set parameters", e);
                                }
                            }
                        }

                        // apply sendrecv, sendonly, recvonly, inactive...
                        if (constraints===true) {
                            constraints = "sendrecv";
                        }
                        switch (constraints) {
                            case "sendrecv":
                            case "sendonly":
                            case "recvonly":
                            case "inactive": {
                                if (!transceiver) {
                                    transceiver = this.getTransceiver(media);
                                }
                                if (transceiver) {
                                    transceiver.direction = constraints;
                                }
                                break;
                            }
                        }

                        // Avoid sending audio if we're holding: this would be recorded!
                        // (Later if we support sending local music-on-hold this might change...)
                        if (media=="audio") {
                            // Just enable track on "recvonly" and "sendrecv";
                            // disable on "inactive" and "sendonly"
                            newTrack.enabled = constraints.includes("recv");
                        }

                    } else {
                        // we're not sending, but maybe we would like to receive the media.
                        // ensure the offer contains the configured direction
                        switch (constraints) {
                            case "recvonly":
                            case "inactive": {
                                if (!transceiver) {
                                    transceiver = this.getTransceiver(media);
                                }
                                if (transceiver) {
                                    transceiver.direction = constraints;
                                }
                                break;
                            }
                        }
                    }
                }

                // ensure we keep audio-stream muted
                if (this.muted && this.localTracks?.audio?.enabled) {
                    this.localTracks.audio.enabled = false;
                }
                
                this.negotiationNeeded = true;
            }

            if (remoteDescription) {
                // An offer may come in while we are busy processing SRD(answer).
                // In this case, we will be in "stable" by the time the offer is processed
                // so it is safe to chain it on our Operations Chain now.
                const readyForOffer = !this.makingOffer && (this.pc.signalingState == "stable" || this.isSettingRemoteAnswerPending);
                const offerCollision = remoteDescription.type == "offer" && !readyForOffer;
        
                this.ignoreOffer = !this.polite && offerCollision;
                if (this.ignoreOffer) {
                    this.processingRtc = false;
                    return;
                }

                // depending on the platform it might be required to tweak SDP
                try {
                    remoteDescription.sdp = this.agent.mapRemoteSdp?.(remoteDescription.sdp) || remoteDescription.sdp;
                } catch(error) {
                    this.agent.error("cannot map remote-sdp",error);
                }
                
                // process the received remote-sdp
                this.isSettingRemoteAnswerPending = remoteDescription.type == "answer";
                await this.pc.setRemoteDescription(remoteDescription); // SRD rolls back as needed
                this.isSettingRemoteAnswerPending = false;

                // if we received an offer
                if (remoteDescription.type == "offer") {
                    const wantsAudioSend = content?.constraints?.audio === true
                        || content?.constraints?.audio === "sendrecv"
                        || content?.constraints?.audio === "sendonly";
                    if (wantsAudioSend) {
                        const audioTransceiver = this.pc.getTransceivers().find(t =>
                            (t.receiver?.track?.kind || t.sender?.track?.kind) === "audio");
                        if (audioTransceiver && !audioTransceiver.sender?.track
                            && audioTransceiver.direction === "recvonly") {
                            this.agent.info(this.callID,"no microphone track but audio send requested, forcing sendrecv to avoid pseudo-hold");
                            audioTransceiver.direction = "sendrecv";
                        }
                    }
                    await this.pc.setLocalDescription();
                    this.negotiationNeeded = true;
                } else if (remoteDescription.type == "answer") {
                    // if we toggle between two calls we have to ensure that
                    // the listeners know that the remote-stream might have changed
                    const videoTransceiver = this.pc.getTransceivers().find(t =>
                        t.sender && t.sender.track && t.sender.track.kind === "video"
                    );
                    const videoRejected = !videoTransceiver || !videoTransceiver.currentDirection;
                    if (videoRejected) {
                        this.agent.debug("video rejected in SDP answer, stopping camera");
                        this.localTracks.video?.stop();
                        this.localTracks.video = undefined;
                        this.localVideoStream = undefined;
                        this.notifyLocalStream();
                    }
                    this.notifyRemoteStream();
                }

            } else if (this.negotiationNeeded) {
                // no remote-sdp received, so we shall create an offer
                this.makingOffer = true;
                let offer = await this.pc.createOffer();
                // depending on the platform it might be required to tweak SDP
                try {
                    offer.sdp = this.agent.mapLocalSdp?.(offer.sdp) || offer.sdp;
                } catch(error) {
                    this.agent.error("cannot map local-sdp",error);
                }
                await this.pc.setLocalDescription(offer);
                this.negotiationNeeded = true;
            }

            if (this.negotiationNeeded) {
                this.sendLocalDescription();
            }
          
            // after processing the message we inform the clients
            if (this.separateStreams) {
                // separate stream for video
                if (this.localTracks.video) {
                    this.localVideoStream ||= new MediaStream();
                    this.addTrack(this.localTracks.video,this.localVideoStream);
                    // localStream keeps remaining audio
                    this.localStream?.removeTrack(this.localTracks.video);
                } else {
                    this.localVideoStream = undefined;
                }
                if (this.localTracks.audio) {
                    this.localAudioStream = this.localStream;
                } else {
                    this.localAudioStream = undefined;
                }
            } else {
                this.localVideoStream = this.localTracks.video?this.localStream:undefined;
                this.localAudioStream = this.localTracks.audio?this.localStream:undefined;
            }
           
            this.setupStreamListeners(this.localAudioStream)
            this.setupStreamListeners(this.localVideoStream)
            this.setupStreamListeners(this.remoteAudioStream)
            this.setupStreamListeners(this.remoteVideoStream)

            this.setupLocalTrackEvents(this.localTracks.video);
            this.setupLocalTrackEvents(this.localTracks.audio);
            this.setupLocalTrackEvents(this.localTracks.display);

            this.notifyLocalStream();
         
	    } catch(err) {
            this.makingOffer = false;
            this.agent.error("could not process event",err)
        }
        this.processingRtc = false;
    }

    // for the moment we log the events for debugging purposes
    private setupStreamListeners(stream?: MediaStream|any) {
        if (stream) {
            // setup listeners for activity
            if (stream.onactive===null) stream.onactive = () => {
                this.agent.debug("stream is now active",stream);
                if (stream==this.localAudioStream || stream==this.localVideoStream) {
                    this.notifyLocalStream();
                }
                if (stream==this.remoteAudioStream || stream==this.remoteVideoStream) {
                    this.notifyRemoteStream();
                }
            };
            if (stream.oninactive===null) stream.oninactive = () => {
                this.agent.debug("stream is now inactive",stream);
                if (stream==this.localAudioStream || stream==this.localVideoStream) {
                    this.notifyLocalStream();
                }
                if (stream==this.remoteAudioStream || stream==this.remoteVideoStream) {
                    this.notifyRemoteStream();
                }
            }
        }
    }

    // When a new track is added to a stream we have to setup
    // listeners for mute/unmute events so we can trigger a
    // "remotestream" event.
    private setupRemoteTrackEvents(track: MediaStreamTrack) {
        track.onmute = (e:any) => {
            this.agent.debug(this.callID,`remote ${track.kind}-track muted`,e);

            // #6065 Screensharing video-stream periodically mutes and unmutes
            // for video-streams it might happen that they are reported muted
            // while the stream is still active -> just ignore
            if (track.kind=="video") return

            this.notifyRemoteStream();
        }
        track.onunmute = (e:any) => {
            this.agent.debug(this.callID,`remote ${track.kind}-track unmuted`,e);
            this.notifyRemoteStream();
        }
    }

    private setupLocalTrackEvents(track?: MediaStreamTrack) {
        if (!track) return
        track.onmute = (e:any) => {
            this.agent.debug(this.callID,`local ${track.kind}-track muted`,e);
            this.notifyLocalStream();
        }
        track.onunmute = (e:any) => {
            this.agent.debug(this.callID,`local ${track.kind}-track unmuted`,e);
            this.notifyLocalStream();
        }
        track.onended = (e:any) => {
            this.agent.debug(this.callID,`local ${track.kind}-track ended`,e);
            this.notifyLocalStream();
        }
    }

    protected notifyLocalStream() {
        this.eventEmitter.notify("localstream",{
            call: this,
            pc: this.pc,
            stream: this.localStream,
            audioStream: this.localAudioStream,
            videoStream: this.localVideoStream
        });
    }

    protected notifyRemoteStream() {
        this.eventEmitter.notify("remotestream",{
            call: this, 
            pc: this.pc, 
            stream: this.remoteStream,
            audioStream: this.remoteAudioStream,
            videoStream: this.remoteVideoStream,
        });
    }

    protected createPeerConnection(configuration: any) {
        let pc: RTCPeerConnection = new RTCPeerConnection(configuration);
        // setup all event-listeners
        pc.addEventListener('track',(event:RTCTrackEvent) => {
            this.agent.debug(this.callID,"track",event);
            const track = event.track;

            let remoteStream = event.streams[0];
            if (!remoteStream) {
                // unbelivable: start call with audio+video and then getting "pranswer"
                // => the track has no stream!!!
                // let's manually create a stream to hear early-media
                remoteStream = new MediaStream();
                this.addTrack(track,remoteStream);
                this.agent.info(`created new stream for ${track.kind} track`,remoteStream);
            }

            // ensure we handle mute/unmute events
            this.setupRemoteTrackEvents(track);

            if (this.separateStreams) {
                if (track.kind=="audio") {
                    if (!this.remoteAudioStream) {
                        this.remoteAudioStream = new MediaStream();
                        this.remoteAudioStream.onremovetrack = (e) => {
                            this.agent.debug(this.callID,"remote audio-track removed",e);
                            this.remoteAudioStream = undefined;
                            this.notifyRemoteStream();
                        }
                    }
                    if (remoteStream!=this.remoteAudioStream) {
                        this.addTrack(track,this.remoteAudioStream);
                        remoteStream.removeTrack(track);
                        this.notifyRemoteStream();
                    }
                } else if (track.kind=="video") {
                    if (!this.remoteVideoStream) {
                        this.remoteVideoStream = new MediaStream();
                        this.remoteVideoStream.onremovetrack = (e) => {
                            this.agent.debug(this.callID,"remote video-track removed",e);
                            if (!this.remoteVideoStream?.getVideoTracks().length) {
                                this.remoteVideoStream = undefined;
                                this.notifyRemoteStream();
                            }
                        }
                    }
                    if (remoteStream!=this.remoteVideoStream) {
                        remoteStream.removeTrack(track);
                        this.addTrack(track,this.remoteVideoStream);
                        this.notifyRemoteStream();
                    }
                }
            } else {
                // setup event-listener to update remote-stream if track ends
                if (remoteStream) {
                    remoteStream.onremovetrack = (e) => {
                        this.agent.debug(this.callID,"remote-track removed",e);
                        this.notifyRemoteStream();
                    }
                    remoteStream.onaddtrack = (event:MediaStreamTrackEvent) => {
                        const track = event.track
                        this.agent.debug(this.callID,"remote-track added",track);
                        this.setupRemoteTrackEvents(track)
                    }
                }                  
                this.remoteStream = remoteStream;
                this.remoteAudioStream = remoteStream?.getAudioTracks().length?remoteStream:undefined;
                this.remoteVideoStream = remoteStream?.getVideoTracks().length?remoteStream:undefined;
                this.notifyRemoteStream();
            }

            if (track.kind=="audio") {
                this.setRemoteAudioTrackProperties(this.remoteAudioTrackProperties);
                this.setAudioReceiverProperties(this.audioReceiverProperties);
            }
            if (track.kind=="video") {
                this.setRemoteVideoTrackProperties(this.remoteVideoTrackProperties);
                this.setVideoReceiverProperties(this.videoReceiverProperties);
            }
        });

        pc.addEventListener('icecandidate', ({candidate}) => {
            if (candidate?.candidate) {
                //:this.agent.debug(this.callID+": icecandidate:",candidate.candidate);
                this.candidates.push(candidate.candidate);
            }
        });

        pc.addEventListener('icegatheringstatechange',(_event:any) => {
            //:this.agent.debug(this.callID,"icegatheringstatechange:",pc.iceGatheringState);
            if (pc.iceGatheringState=="complete" && this.candidatePolicy!="none") {
                this.sendLocalDescription(this.candidates);
            }
        });

        // https://developer.mozilla.org/en-US/docs/Web/API/WebRTC_API/Session_lifetime#ice_restart
        // try restart ICE-session after NIC changed
        pc.oniceconnectionstatechange = (event:any) => {
            this.agent.debug(this.callID,"iceconnectionstatechange:",pc.iceConnectionState,event);
            if (pc.iceConnectionState === "failed") {
                this.agent.info(this.callID,"restarting ICE");
                pc.setConfiguration(configuration);
                pc.restartIce();
            }
        };

        return pc;
    }

    protected deletePeerConnection() {
        if (this.pc) {
            // android does not have pc.getSenders
            if (this.pc.getSenders) {
                this.pc.getSenders().forEach((sender:any) => this.pc?.removeTrack(sender));

                // android fails if we call stop -> do it here
                for (let media in this.localTracks) {
                    this.localTracks[media as keyof LocalTracks]?.stop();
                }
            }
            this.pc.close();
            for (let media in this.localTracks) {
                this.localTracks[media as keyof LocalTracks] = undefined;
            }
            this.pc = undefined;
            
            if (this.agent.externalAudio) {
                this.agent.externalAudio({
                    callID: this.callID,
                    type: "close",
                });
            }

            // ensure tracks are stopped
            this.stopAndRemoveTracks(this.localStream);
            this.stopAndRemoveTracks(this.localAudioStream);
            this.stopAndRemoveTracks(this.localVideoStream);
            this.stopAndRemoveTracks(this.remoteStream);
            this.stopAndRemoveTracks(this.remoteAudioStream);
            this.stopAndRemoveTracks(this.remoteVideoStream);
            
            this.localStream = undefined;
            this.localAudioStream = undefined;
            this.localVideoStream = undefined;

            this.remoteStream = undefined;
            this.remoteAudioStream = undefined;
            this.remoteVideoStream = undefined;
            
            this.notifyRemoteStream();

            this.eventEmitter.notify('localstream',{call: this});
        }
    }

    

    protected async sendLocalDescription(candidates:any = undefined) {
        let sdp = this.pc?.localDescription?.sdp;
        let type = this.pc?.localDescription?.type;

        if (this.externalAudioSessionDescription) {
            const audioDescription = await this.externalAudioSessionDescription;
            if (audioDescription?.sdp) {

                const description: DescriptionType = {
                    common: [],
                    audio: [],
                    video: []
                }

                // always parse audio SDP to get audioDirection
                this.audioDirection = "unknown";

                let part = "common";
                audioDescription.sdp.split(/[\n\r]+/).forEach(line => {
                    if (this.filterSdpLine(line)) return;   
                    if (line.startsWith("m=audio")) {
                        part = "audio";
                    }
                    part=="audio" && description.audio.push(line);
                    switch(line) {
                        case "a=sendrecv": 
                        case "a=sendonly": 
                        case "a=recvonly": 
                        case "a=inactive": 
                            this.audioDirection = line.substring(2);
                    }
                });

                if (sdp) {
                    // destruct the video and use the common-part
                    part = "common";
                    sdp.split(/[\n\r]+/).forEach(line => {
                        if (this.filterSdpLine(line)) return; 
                        if (line.startsWith("m=video")) {
                            part = "video";
                        }
                        description[part as keyof DescriptionType].push(line);
                    });

                    // Now we merge the audio and video SDPs.
                    // Maybe we should care about the m= order, but for now 
                    // it seems it is always audio then video.
                    sdp = this.buildSdpBlock(description.common)+
                        this.buildSdpBlock(description.audio)+
                        this.buildSdpBlock(description.video);
                } else {
                    // no video SDP, just use audio SDP as we received it
                    sdp = audioDescription.sdp;
                    type = audioDescription.type;
                }
            }
            this.externalAudioSessionDescription = undefined;
        } else if (sdp) {
            // to be consisten with the external-audio case we also derive
            // the audioDirection from the send local SDP
            this.audioDirection = "unknown";

            let audio = false;
            sdp.split(/[\n\r]+/).forEach(line => {
                if (line.startsWith("m=")) {
                    audio = line.startsWith("m=audio")
                }
                if (audio) {
                    switch(line) {
                        case "a=sendrecv": 
                        case "a=sendonly": 
                        case "a=recvonly": 
                        case "a=inactive": 
                            this.audioDirection = line.substring(2);
                    }
                }
            });
        }

        // apply sdp-mapping if requested
        try {
            sdp = this.agent.mapLocalSdp?.(sdp) || sdp;
        } catch(error) {
            this.agent.error("cannot map local-sdp",error);
        }

        this.negotiationNeeded = false;
        this.agent.send({
            RtcMessage: {
                connection: {
                    callID: this.callID,
                    deviceID: this.device.deviceID
                },
                localDescription: { sdp, type },
                candidates,
            }});
        this.makingOffer = false;
    }

    private filterSdpLine(line: string) {
        if (line=="") return true;
        if (line.startsWith("a=mid:")) return true;
        if (line.startsWith("a=msid:")) return true;
        if (line.startsWith("a=msid-semantic:")) return true;
        if (line.startsWith("a=group:")) return true;
        if (line.startsWith("a=extmap:") && line.includes("urn:ietf:params:rtp-hdrext:sdes:mid")) return true;
        return false;
    }

    private buildSdpBlock(block: string[]): string {
        return block.length?block.join("\r\n")+"\r\n":"";
    }

    protected debugTracks(text: string) {
        this.agent.debug(text);   
        this.pc?.getTransceivers().forEach(t => {
            this.agent.debug(`⇒ ${t.sender?.track?.kind} sender   `,t.sender.track);
            this.agent.debug(`⇒ ${t.receiver?.track?.kind} receiver `,t.receiver.track);
        });
    }

    private addTrack(track: MediaStreamTrack,stream: MediaStream) {
        if (!stream.getTrackById(track.id)) {
            // remove other tracks in this stream with the same kind
            stream.getTracks().forEach(t => {
                if (t.kind==track.kind) {
                    t.stop();
                    stream.removeTrack(t);
                }
            });
            stream.addTrack(track);
        }
    }

    private stopAndRemoveTracks(stream?: MediaStream) {
        stream?.getTracks().forEach(track => {
            track.stop();
            stream.removeTrack(track);
        });
    }

    async getVideoStats(): Promise<any> {
        return new Promise((resolve,reject) => {
            this.pc?.getStats().then(stats => {
                const result: any = {}
                let report: RTCInboundRtpStreamStats|undefined;
                stats?.forEach(r => {
                    if (r.type=="inbound-rtp" && r.kind=="video") {
                        report = r;
                    }
                });
                if (report) {
                    result.frameRate = report.framesPerSecond || 0;
                    result.resolution = (report.frameWidth || report.frameHeight) ? `${report.frameWidth}x${report.frameHeight}`: '-';

                    // calculate loss
                    const packetsReceived = report.packetsReceived || 0;
                    const packetsLost = report.packetsLost || 0;

                    if (packetsReceived+packetsLost > 0) {
                        result.packetLoss = Math.round(100 * packetsLost / (packetsReceived+packetsLost));
                    } else {
                        result.packetLoss = 100;
                    }

                    // calculate quality based on packetLoss
                    if (result.packetLoss<2) {
                        result.quality = 100;
                    } else if (result.packetLoss<4) {
                        result.quality = 80;
                    } else if (result.packetLoss<6) {
                        result.quality = 60;
                    } else if (result.packetLoss<8) {
                        result.quality = 40;
                    } else if (result.packetLoss<12) {
                        result.quality = 20;
                    } else {
                        result.quality = 0;
                    }
                    resolve(result);
                } else {
                    resolve(undefined);
                }
            }).catch(error => {
                reject(error);
            });
        });    
    } 

}
